import { useToast } from "@chakra-ui/react";
import { useEffect, useRef } from "react";

import { ImportTaskBroDomainEnum } from "@nens/brostar/openapi-client";

import { useInvalidateGldQueries } from "../queries/query-gld";
import { useInvalidateGmnQueries } from "../queries/query-gmn";
import { useInvalidateGmwQueries } from "../queries/query-gmw";
import { useInvalidateGpdQueries } from "../queries/query-gpd";
import { useImportTasksLoadingStatus } from "../queries/query-import-task";

/**
 * Component that monitors import task status and shows toasts on completion.
 * Also triggers refetch of related data when tasks complete.
 */
export function ImportTaskMonitor() {
  const toast = useToast();
  const { loadingDomains } = useImportTasksLoadingStatus();
  const previousLoadingDomainsRef = useRef<(ImportTaskBroDomainEnum | undefined)[]>([]);
  const invalidateGldListQuery = useInvalidateGldQueries();
  const invalidateGpdQueries = useInvalidateGpdQueries();
  const invalidateGmwQueries = useInvalidateGmwQueries();
  const invalidateGmnQueries = useInvalidateGmnQueries();

  useEffect(() => {
    // Find domains that were loading but are no longer loading (completed)
    const completedDomains = previousLoadingDomainsRef.current.filter(
      (domain) => !loadingDomains.includes(domain)
    );

    // Show success toast and trigger refetch for each completed domain
    completedDomains.forEach((domain) => {
      toast({
        title: `Successfully imported ${domain}`,
        status: "success",
        duration: 9000,
        isClosable: true,
      });

      const refetchMap: Record<ImportTaskBroDomainEnum, () => void> = {
        GMW: invalidateGmwQueries,
        GMN: invalidateGmnQueries,
        GLD: invalidateGldListQuery,
        // FUTURE
        // These domains are still in development. The back-end is not finished yet, but the foundation is there.
        // We are awaiting further backing before we fully implement the front-end.
        FRD: () => undefined,
        GAR: () => undefined,
        CPT: () => undefined,
        BHR: () => undefined,
        GPD: invalidateGpdQueries,
        GUF: () => undefined,
      };

      if (domain) refetchMap[domain]();
    });

    previousLoadingDomainsRef.current = loadingDomains;
  }, [
    loadingDomains,
    toast,
    invalidateGldListQuery,
    invalidateGpdQueries,
    invalidateGmwQueries,
    invalidateGmnQueries,
  ]);

  return null;
}
